import { useCallback, useMemo } from 'react';
import { UploadFile } from '@/components/file/upload/UploadFile';
import {
  type DeviceFile,
  useAddDeviceFile,
  useDeleteDeviceFile,
  useDeviceFileDownload,
  useDeviceFiles,
} from '@/hooks/device';
import { useFileDeleteHandler } from '@/hooks/file';
import { useCanWriteById } from '@/hooks/workspace';
import { deviceFileTableColumns } from '../common/table/column_definitions/DeviceFile';
import { TableStoreProvider } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { DeviceFileContent } from './DeviceFileContent';

type Props = {
  workspaceId: number;
  deviceId: number;
  actionOnErr?: () => void;
};

export const DeviceFileTable = ({ workspaceId, deviceId, actionOnErr }: Props) => {
  const canWrite = useCanWriteById(workspaceId);

  const { data, isLoading, isValidating } = useDeviceFiles(deviceId, workspaceId);

  const addFileMutation = useAddDeviceFile(deviceId, workspaceId);
  const addFile = useCallback(
    async (data: { fileName: string; fileContents: string }) => {
      return addFileMutation({
        ...data,
        replaceIfExists: true,
      });
    },
    [addFileMutation]
  );
  const deleteFile = useDeleteDeviceFile(deviceId, workspaceId, actionOnErr);
  const handleDownload = useDeviceFileDownload(workspaceId, deviceId, actionOnErr);
  const { deletingIds, handleDelete } = useFileDeleteHandler(deleteFile);

  const columns = useMemo(
    () => deviceFileTableColumns(canWrite ? handleDelete : undefined, handleDownload),
    [canWrite, handleDelete, handleDownload]
  );

  const filteredFiles = useMemo(() => {
    const files = data?.deviceFiles || [];
    return files.filter((f) => !deletingIds.has(f.id));
  }, [data?.deviceFiles, deletingIds]) as DeviceFile[];

  return (
    <>
      <TableStoreProvider initialSorting={[{ desc: true, id: 'id' }]} name="DeviceFiles">
        <Table<DeviceFile>
          columns={columns}
          count={filteredFiles.length}
          data={filteredFiles}
          expandableRow={({ original: { id } }) => (
            <DeviceFileContent deviceId={deviceId} fileId={id} workspaceId={workspaceId} />
          )}
          isLoading={Boolean(isLoading || !data)}
          isValidating={isValidating}
          onRowClick={(_, row) => row.toggleExpanded()}
          title="Files"
        />
      </TableStoreProvider>
      {canWrite && <UploadFile fileType="device" onUpload={addFile} />}
    </>
  );
};
